import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  FormControlLabel,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  acceptAllCookies,
  cookieCategories,
  optionalCookieCategories,
  readCookiePreferences,
  rejectOptionalCookies,
  saveCookiePreferences,
} from '../../privacy/cookieConsent';

const CookiePreferencesPanel = ({ compact = false, onSaved }) => {
  const { t } = useTranslation();
  const [preferences, setPreferences] = useState(() => readCookiePreferences());
  const [saved, setSaved] = useState(false);

  const formState = useMemo(
    () => ({
      analytics: Boolean(preferences?.analytics),
      performance: Boolean(preferences?.performance),
      marketing: Boolean(preferences?.marketing),
    }),
    [preferences]
  );

  useEffect(() => {
    const handleUpdate = (event) => {
      setPreferences(event.detail || readCookiePreferences());
    };

    window.addEventListener('thetavern:cookie-consent-updated', handleUpdate);

    return () => {
      window.removeEventListener('thetavern:cookie-consent-updated', handleUpdate);
    };
  }, []);

  const updatePreference = (category, enabled) => {
    setSaved(false);
    setPreferences((current) => ({
      ...(current || {}),
      ...formState,
      [category]: enabled,
      essential: true,
    }));
  };

  const handleSave = () => {
    const nextPreferences = saveCookiePreferences(formState);

    setPreferences(nextPreferences);
    setSaved(true);
    onSaved?.(nextPreferences);
  };

  const handleAcceptAll = () => {
    const nextPreferences = acceptAllCookies();

    setPreferences(nextPreferences);
    setSaved(true);
    onSaved?.(nextPreferences);
  };

  const handleRejectOptional = () => {
    const nextPreferences = rejectOptionalCookies();

    setPreferences(nextPreferences);
    setSaved(true);
    onSaved?.(nextPreferences);
  };

  return (
    <Stack spacing={compact ? 2 : 2.5}>
      <Box>
        <Typography variant={compact ? 'h4' : 'h3'}>
          {t('cookies.preferences.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
          {t('cookies.preferences.subtitle')}
        </Typography>
      </Box>

      <Stack spacing={1.5}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={2}>
          <Box>
            <Typography variant="subtitle2">
              {t(cookieCategories.essential.labelKey)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t(cookieCategories.essential.descriptionKey)}
            </Typography>
          </Box>
          <Chip label={t('cookies.preferences.alwaysOn')} color="primary" size="small" />
        </Stack>

        <Divider />

        {optionalCookieCategories.map((category) => (
          <FormControlLabel
            key={category}
            control={
              <Switch
                checked={Boolean(formState[category])}
                onChange={(event) => updatePreference(category, event.target.checked)}
              />
            }
            label={
              <Box>
                <Typography variant="subtitle2">
                  {t(cookieCategories[category].labelKey)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t(cookieCategories[category].descriptionKey)}
                </Typography>
              </Box>
            }
            sx={{
              alignItems: 'flex-start',
              ml: 0,
              gap: 1,
              '& .MuiFormControlLabel-label': { flex: 1 },
            }}
          />
        ))}
      </Stack>

      {saved ? <Alert severity="success">{t('cookies.preferences.saved')}</Alert> : null}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
        <Button variant="contained" onClick={handleSave}>
          {t('cookies.preferences.save')}
        </Button>
        <Button variant="outlined" onClick={handleRejectOptional}>
          {t('cookies.actions.reject')}
        </Button>
        <Button variant="text" onClick={handleAcceptAll}>
          {t('cookies.actions.accept')}
        </Button>
      </Stack>
    </Stack>
  );
};

export default CookiePreferencesPanel;
